import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  DragDropContext,
  DropResult,
  Droppable,
  DragStart,
} from 'react-beautiful-dnd';

import { multiSelect, multiDragAwareReorder } from '../../utils';

import SingleColumn from '../SingleColumn';
import { getSelectedItemIds } from '../../../../selectors';
import { getSearchState } from '../../../../../Search/selectors';
import { filterColumns } from '../../../../utils';
import { IColumnWithUuid } from '../../types';
import DraggingClone from '../DraggingClone';
import { setSelectedItemsIds as setSelectedItemsIdsAction } from '../../../../actions';

interface Props {
  checkIfOrderChanged: (item: IColumnWithUuid, itemIndex?: number) => boolean;
  items: IColumnWithUuid[];
  updateDatasetColumns: (newColumns: IColumnWithUuid[]) => void;
  dragDropId: string;
}

const ReorderDragAndDrop: React.FC<Props> = ({
  checkIfOrderChanged,
  dragDropId,
  items,
  updateDatasetColumns,
}) => {
  const dispatch = useDispatch();
  const selectedItemsIds = useSelector(getSelectedItemIds);
  const { searchQuery } = useSelector(getSearchState);

  const setSelectedItemsIds = (ids: string[]) =>
    dispatch(setSelectedItemsIdsAction(ids));
  const [draggingTaskId, setDraggingTaskId] = useState<string>(null);

  const filteredItems = useMemo(
    () => filterColumns(items, searchQuery),
    [items, searchQuery]
  );

  const unselectAll = () => {
    setSelectedItemsIds([]);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return;
      }

      if (event.key === 'Escape') {
        unselectAll();
      }
    };

    const handleClickOrTouchEnd = (event: MouseEvent | TouchEvent) => {
      if (event.defaultPrevented) {
        return;
      }

      unselectAll();
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('click', handleClickOrTouchEnd);
    window.addEventListener('touchend', handleClickOrTouchEnd);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('click', handleClickOrTouchEnd);
      window.removeEventListener('touchend', handleClickOrTouchEnd);
    };
  }, []);

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    const { processedItems } = multiDragAwareReorder({
      items,
      selectedItemsIds,
      source,
      destination,
    });

    setDraggingTaskId(null);
    updateDatasetColumns(processedItems);
  };

  const handleDragStart = (result: DragStart) => {
    const id = result.draggableId;

    const isSelected = selectedItemsIds.some((itemId) => itemId === id);

    // if dragging an item that is not selected - unselect all items
    if (!isSelected) {
      unselectAll();
    }

    setDraggingTaskId(id);
  };

  const toggleSelection = (itemId: string) => {
    const wasSelected = selectedItemsIds.includes(itemId);

    const newTaskIds = (() => {
      // Task was not previously selected
      // now will be the only selected item
      if (!wasSelected) {
        return [itemId];
      }

      // Task was part of a selected group
      // will now become the only selected item
      if (selectedItemsIds.length > 1) {
        return [itemId];
      }

      // task was previously selected but not in a group
      // we will now clear the selection
      return [];
    })();

    setSelectedItemsIds(newTaskIds);
  };
  const toggleSelectionInGroup = (itemId: string) => {
    const index = selectedItemsIds.indexOf(itemId);

    // if not selected - add it to the selected items
    if (index === -1) {
      setSelectedItemsIds([...selectedItemsIds, itemId]);
      return;
    }

    // it was previously selected and now needs to be removed from the group
    const shallowCopy = [...selectedItemsIds];
    shallowCopy.splice(index, 1);
    setSelectedItemsIds(shallowCopy);
  };

  const multiSelectTo = (itemId: string) => {
    const updated = multiSelect(items, selectedItemsIds, itemId);

    if (!updated) return;

    setSelectedItemsIds(updated);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
      <Droppable
        droppableId={dragDropId}
        renderClone={(provided) => (
          <div
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...provided.draggableProps}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...provided.dragHandleProps}
            ref={provided.innerRef}
          >
            {/* react-beautiful-dnd is doing constant rerenders of this component :C */}
            {React.useMemo(
              () => (
                <DraggingClone
                  selectionCount={selectedItemsIds.length}
                  draggingTaskId={draggingTaskId}
                />
              ),
              [draggingTaskId]
            )}
          </div>
        )}
      >
        {(provided: any) => (
          <div ref={provided.innerRef} data-testid="columns-reorder-wrapper">
            {filteredItems.map((item, index) => {
              const isSelected = selectedItemsIds.includes(item.uuid);
              const isGhosting =
                isSelected && !!draggingTaskId && draggingTaskId !== item.uuid;

              return (
                <SingleColumn
                  key={item.uuid}
                  column={item}
                  index={index}
                  multiSelectTo={multiSelectTo}
                  toggleSelectionInGroup={toggleSelectionInGroup}
                  toggleSelection={toggleSelection}
                  isSelected={isSelected}
                  isGhosting={isGhosting}
                  hasOrderChanged={checkIfOrderChanged(item, index)}
                />
              );
            })}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default ReorderDragAndDrop;
